// Pure derivation helpers for the fleet site card (#565).
// Honesty rules (spec 2026-07-03 §6): running is derived from produced power,
// never from breaker_closed (untrustworthy until Demsv1.0 #1074); missing
// data yields null so the card renders an em-dash, never a fabricated zero.

import type { GeneratorRow } from '../../types/project.types';
import type { Site } from '../../hooks/useSites';
import type { FleetLiveEntry } from '../../hooks/useFleetTelemetry';

export type CardState = 'commissioning' | 'offline' | 'live' | 'stale' | 'connecting';
export type TelemetryPresence = 'live' | 'stale' | 'none';

export const STALE_AFTER_MS = 30_000;
const RUNNING_FLOOR_KW = 5;
const BESS_IDLE_DEADBAND_KW = 5;

export function deriveCardState(
  site: Site,
  live: FleetLiveEntry | undefined,
  nowMs: number
): CardState {
  if (site.commissioning_status === 'commissioning') return 'commissioning';
  if (site.connection_status === 'offline' || site.status === 'offline') return 'offline';
  const presence = deriveTelemetryPresence(live, nowMs);
  return presence === 'none' ? 'connecting' : presence;
}

// Freshness of the live summary alone, independent of the card state. The
// commissioning flag is a go-live decision, not a telemetry fact: a site can
// stream while still commissioning (#789), so the panel reads this instead of
// assuming silence.
export function deriveTelemetryPresence(
  live: FleetLiveEntry | undefined,
  nowMs: number
): TelemetryPresence {
  if (!live) return 'none';
  return nowMs - live.lastUpdatedAt > STALE_AFTER_MS ? 'stale' : 'live';
}

export function pickNum(
  summary: Record<string, unknown> | undefined,
  ...keys: string[]
): number | null {
  if (!summary) return null;
  for (const k of keys) {
    const v = summary[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
  }
  return null;
}

export interface GenStats {
  running: number;
  total: number;
  avgLoadPct: number | null;
}

export function computeGenStats(
  gens: GeneratorRow[],
  perGenNameplateKw: number | null
): GenStats {
  const threshold = Math.max(RUNNING_FLOOR_KW, 0.01 * (perGenNameplateKw ?? 0));
  const running = gens.filter((g) => g.kw != null && g.kw > threshold);
  const loads = running
    .map((g) => g.engine_load_pct)
    .filter((v): v is number => v != null && Number.isFinite(v));
  return {
    running: running.length,
    total: gens.length,
    avgLoadPct: loads.length ? loads.reduce((s, v) => s + v, 0) / loads.length : null,
  };
}

export type BessFlow = 'charging' | 'discharging' | 'idle';

// Sign convention matches the edge aggregate (sum of per-BMS active_power_kw):
// discharge-positive. Small residuals inside the deadband render as idle.
export function bessFlow(bessPowerKw: number): BessFlow {
  if (bessPowerKw > BESS_IDLE_DEADBAND_KW) return 'discharging';
  if (bessPowerKw < -BESS_IDLE_DEADBAND_KW) return 'charging';
  return 'idle';
}

// 1240 → "1.24", 1600 → "1.6" (trailing zeros stripped, matches old card fmt)
export function formatMw(kw: number): string {
  return Number((kw / 1000).toFixed(2)).toString();
}
